// Thin fetch wrapper: attaches the Bearer token, and on a 401 for an auth-required call it refreshes
// once and replays. If refresh fails, throws AuthRequiredError so the widget can re-open login.
import { Auth, AuthRequiredError } from './auth';
import type { EmbedConfig } from './types';
import { handleFetchFailure } from './maintenance';

export class Api {
  constructor(private cfg: EmbedConfig, private auth: Auth) {}

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  /** POST. requireAuth=true triggers the 401→refresh→replay path. */
  post<T>(path: string, body?: unknown, requireAuth = true): Promise<T> {
    return this.request<T>('POST', path, body, requireAuth);
  }

  /** PUT. requireAuth=true triggers the 401→refresh→replay path. */
  put<T>(path: string, body?: unknown, requireAuth = true): Promise<T> {
    return this.request<T>('PUT', path, body, requireAuth);
  }

  private async request<T>(method: string, path: string, body?: unknown, requireAuth = false): Promise<T> {
    const doFetch = (): Promise<Response> => {
      const headers: Record<string, string> = {};
      if (body !== undefined) headers['Content-Type'] = 'application/json';
      const token = this.auth.token;
      if (token) headers['Authorization'] = `Bearer ${token}`;
      return fetch(`${this.cfg.baseApiUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    };

    let res: Response;
    try {
      res = await doFetch();
    } catch (e) {
      // No answer at all: the API is mid-deploy (stopped), or the customer is offline. Only the former
      // becomes the maintenance card; the latter is rethrown for the widget's usual error path.
      const down = await handleFetchFailure(null);
      throw down ?? e;
    }

    if (res.status === 503) {
      const down = await handleFetchFailure(res);
      if (down) throw down;
    }

    if (res.status === 401 && requireAuth) {
      const refreshed = await this.auth.refresh();
      if (refreshed) res = await doFetch();
      else throw new AuthRequiredError();
    }
    if (res.status === 401) throw new AuthRequiredError();

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`365-embed API ${res.status}: ${text.slice(0, 200)}`);
    }

    if (res.status === 204) return undefined as unknown as T;
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) return undefined as unknown as T;
    return (await res.json()) as T;
  }
}
